import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["indicator", "avatars", "count", "scrollButton"]
  static values = { 
    checkInterval: { type: Number, default: 30000 } // 30 seconds
  }

  connect() {
    console.log('🆕 New Posts Float Controller connected')
    
    // Initialize arrays to track new posts
    this.newPosts = []
    this.newAuthors = []
    
    // Handle scroll events for scroll-to-top functionality
    this.handleScroll = this.handleScroll.bind(this)
    window.addEventListener('scroll', this.handleScroll)
    this.handleScroll() // Check initial state
    
    // Listen for Turbo Stream updates
    this.setupTurboStreamListener()
    
    // Set initial timestamp
    this.lastCheckTime = new Date().toISOString()
  }

  disconnect() {
    window.removeEventListener('scroll', this.handleScroll)
    if (this.observer) {
      this.observer.disconnect()
    }
  }
  
  setupTurboStreamListener() {
    // Use MutationObserver to watch for new posts notifications added by Turbo Streams
    const target = document.getElementById('new-posts-notifications')
    if (!target) return
    
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1 && node.dataset.newPostAuthor) {
            // Parse the author data
            const authorData = JSON.parse(node.dataset.newPostAuthor)
            const postId = node.dataset.postId
            
            // Only show notification if it's not from the current user
            const currentUserId = document.querySelector('meta[name="current-user-id"]')?.content
            if (currentUserId && authorData.id.toString() === currentUserId) {
              return
            }
            
            // Add to our tracking arrays (avoid duplicates)
            if (!this.newPosts.includes(postId)) {
              this.newPosts.push(postId)
              
              // Check if author already in list
              const existingAuthor = this.newAuthors.find(a => a.id === authorData.id)
              if (!existingAuthor) {
                this.newAuthors.push(authorData)
              }
              
              // Update the notification display
              this.showNewPostsIndicator(this.newAuthors, this.newPosts.length)
            }
            
            // Remove the notification element to keep DOM clean
            node.remove()
          }
        })
      })
    })
    
    this.observer.observe(target, { childList: true })
  }

  isPostsIndexPage() {
    // Check if we're on the posts index page
    return window.location.pathname === '/' || window.location.pathname === '/posts'
  }

  isFirstPage() {
    const urlParams = new URLSearchParams(window.location.search)
    const page = urlParams.get('page')
    return !page || page === '1'
  }

  handleScroll() {
    const scrolled = window.pageYOffset > 300
    
    // Only show scroll button if there are no new posts
    if (scrolled && !this.hasNewPosts) {
      this.showScrollButton()
    } else if (!scrolled) {
      this.hideScrollButton()
    }
  }

  showScrollButton() {
    if (!this.hasScrollButtonTarget) return
    
    this.scrollButtonTarget.classList.remove('hidden', 'opacity-0', 'pointer-events-none')
    this.scrollButtonTarget.classList.add('opacity-100')
  }

  hideScrollButton() {
    if (!this.hasScrollButtonTarget) return
    
    this.scrollButtonTarget.classList.add('opacity-0', 'pointer-events-none')
    setTimeout(() => {
      this.scrollButtonTarget.classList.add('hidden')
    }, 300)
  }


  showNewPostsIndicator(authors, count) {
    if (!this.hasIndicatorTarget) return
    
    this.hasNewPosts = true
    
    // Hide scroll button if visible
    this.hideScrollButton()
    
    // Update count
    if (this.hasCountTarget) {
      this.countTarget.textContent = count
    }
    
    // Update avatars (limit to 3 for space)
    if (this.hasAvatarsTarget) {
      const displayAuthors = authors.slice(0, 3)
      this.avatarsTarget.innerHTML = displayAuthors.map(author => {
        const displayName = this.escapeHtml(author.display_name)
        const avatarUrl = this.safeUrl(author.avatar_url)
        if (avatarUrl) {
          const orgClasses = "org-logo org-logo--tiny max-h-6 max-w-[5rem] w-auto h-auto object-contain border-2 border-white dark:border-night-surface ring-2 ring-blue-600 dark:ring-night-primary"
          const personClasses = "w-10 h-10 rounded-full border-2 border-white dark:border-night-surface ring-2 ring-blue-600 dark:ring-night-primary object-cover"
          return `<img src="${avatarUrl}"
                       alt="${displayName}"
                       class="${author.is_organization ? orgClasses : personClasses}"
                       title="${displayName}">`
        } else {
          // Show initials for users without avatars
          const rawInitial = author.display_initial || (author.display_name || "").charAt(0).toUpperCase()
          const initial = this.escapeHtml(rawInitial)
          return `<div class="w-10 h-10 rounded-full border-2 border-white dark:border-night-surface ring-2 ring-blue-600 dark:ring-night-primary bg-blue-500 dark:bg-night-primary flex items-center justify-center text-white font-bold text-sm"
                       title="${displayName}">
                    ${initial}
                  </div>`
        }
      }).join('')
    }
    
    // Show the indicator with animation
    this.indicatorTarget.classList.remove('hidden', 'opacity-0')
    this.indicatorTarget.style.opacity = '0'
    this.indicatorTarget.style.transform = 'translateY(10px)'
    
    requestAnimationFrame(() => {
      this.indicatorTarget.style.transition = 'all 0.3s ease-out'
      this.indicatorTarget.style.opacity = '1'
      this.indicatorTarget.style.transform = 'translateY(0)'
    })
    
    console.log(`📢 Showing ${count} new posts from:`, authors.map(a => a.display_name))
  }

  async loadNewPostsAndScroll() {
    try {
      // First scroll to top smoothly
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      })
      
      // Hide the indicator and reset tracking
      this.indicatorTarget.classList.add('hidden')
      this.hasNewPosts = false
      
      // Wait a bit for scroll to start
      setTimeout(async () => {
        // Fetch new posts based on our tracked post IDs
        if (this.newPosts.length === 0) {
          console.log('No new posts to load')
          return
        }
        
        const response = await fetch(`/posts/load_new_posts?since=${encodeURIComponent(this.lastCheckTime)}`, {
          headers: {
            'Accept': 'text/vnd.turbo-stream.html',
            'X-Requested-With': 'XMLHttpRequest'
          }
        })

        if (response.ok) {
          const turboStreamContent = await response.text()
          Turbo.renderStreamMessage(turboStreamContent)
          
          // Clear our tracking arrays
          this.newPosts = []
          this.newAuthors = []
          
          // Update lastCheckTime to now
          this.lastCheckTime = new Date().toISOString()
          console.log('🕒 Updated lastCheckTime after loading posts:', this.lastCheckTime)
          
          // Show success message
          this.showRefreshSuccess()
          
          console.log('📈 Prepended new posts to the feed')
        }
      }, 300)
      
    } catch (error) {
      console.error('Error loading new posts:', error)
    }
  }
  
  showRefreshSuccess() {
    // Create a temporary success message
    const successMsg = document.createElement('div')
    successMsg.className = 'fixed top-20 left-1/2 transform -translate-x-1/2 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 transition-all duration-300'
    successMsg.innerHTML = '<i class="fas fa-check mr-2"></i>New posts loaded!'
    successMsg.style.opacity = '0'
    successMsg.style.transform = 'translate(-50%, -10px)'
    
    document.body.appendChild(successMsg)
    
    // Animate in
    requestAnimationFrame(() => {
      successMsg.style.opacity = '1'
      successMsg.style.transform = 'translate(-50%, 0)'
    })
    
    // Remove after 2 seconds
    setTimeout(() => {
      successMsg.style.opacity = '0'
      successMsg.style.transform = 'translate(-50%, -10px)'
      setTimeout(() => {
        document.body.removeChild(successMsg)
      }, 300)
    }, 2000)
  }

  // Escape for safe interpolation into text and double-quoted attributes.
  escapeHtml(text) {
    return String(text == null ? "" : text).replace(/[&<>"']/g, (c) => (
      { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
    ))
  }

  // Allow only safe http(s)/relative URLs for avatar src; reject javascript: etc.
  safeUrl(url) {
    if (!url) return ""
    const raw = String(url).trim()
    if (/^(https?:\/\/|tel:|mailto:|\/)/i.test(raw)) {
      return raw.replace(/"/g, "%22").replace(/</g, "%3C").replace(/>/g, "%3E")
    }
    return ""
  }
}